import { parseTxError } from './txErrors';
import { getGasFees } from './gasFees';

const GAS_BUFFER = 1.25;
const MAX_GAS_LIMIT = 5_000_000;

/**
 * Estimate gas, then send a contract tx.
 * Gas is estimated via the wallet provider with a buffer and sane cap —
 * avoids web3's ~140M default when auto-estimation fails (RPC rejects >25M).
 *
 * Must NOT return .send() from an async function — PromiEvent is thenable and
 * would be awaited, stripping .on() and breaking event listeners.
 */
export function sendContractTransaction(
  contractMethod,
  { from, gasFallback = 300_000, web3 },
  { onStatus, successMessage }
) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let txHash = null;

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      fn(value);
    };

    const attachListeners = (promi) => {
      promi
        .on('transactionHash', (hash) => {
          txHash = hash;
          onStatus?.({
            phase: 'pending',
            message: 'Transaction submitted — waiting for confirmation…',
            txHash: hash,
          });
        })
        .on('receipt', (receipt) => {
          if (receipt.status === false || receipt.status === '0') {
            const err = new Error('Transaction reverted on-chain');
            onStatus?.({ phase: 'error', message: parseTxError(err), txHash });
            finish(reject, err);
            return;
          }
          onStatus?.({
            phase: 'success',
            message: successMessage || 'Transaction confirmed!',
            txHash,
          });
          finish(resolve, receipt);
        })
        .on('error', (error) => {
          const message = parseTxError(error);
          onStatus?.({ phase: 'error', message, txHash });
          finish(reject, error);
        })
        .catch((error) => {
          const message = parseTxError(error);
          onStatus?.({ phase: 'error', message, txHash });
          finish(reject, error);
        });
    };

    onStatus?.({
      phase: 'signing',
      message: 'Confirm the transaction in your wallet…',
      txHash: null,
    });

    // Resolve a sane gas limit, then attach live Base fees before sending so the
    // wallet doesn't default to a padded gas price.
    const sendWithGas = (gas) => {
      Promise.resolve(getGasFees(web3)).then((fees) => {
        attachListeners(contractMethod.send({ from, gas, ...fees }));
      }).catch((error) => {
        onStatus?.({ phase: 'error', message: parseTxError(error), txHash });
        finish(reject, error);
      });
    };

    contractMethod
      .estimateGas({ from })
      .then((estimated) => {
        const withBuffer = Math.ceil(Number(estimated) * GAS_BUFFER);
        sendWithGas(Math.min(withBuffer, MAX_GAS_LIMIT));
      })
      .catch((error) => {
        console.warn('Gas estimate failed, using fallback:', error?.message || error);
        sendWithGas(gasFallback);
      });
  });
}

/** @deprecated Use sendContractTransaction — kept for any external callers */
export function sendContractTx(sendPromise, opts) {
  return new Promise((resolve, reject) => {
    if (!sendPromise || typeof sendPromise.on !== 'function') {
      reject(new Error('Invalid transaction — internal send error'));
      return;
    }
    let settled = false;
    let txHash = null;
    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      fn(value);
    };
    sendPromise
      .on('transactionHash', (hash) => {
        txHash = hash;
        opts.onStatus?.({ phase: 'pending', message: 'Transaction submitted…', txHash: hash });
      })
      .on('receipt', (receipt) => {
        if (receipt.status === false || receipt.status === '0') {
          const err = new Error('Transaction reverted on-chain');
          opts.onStatus?.({ phase: 'error', message: parseTxError(err), txHash });
          finish(reject, err);
          return;
        }
        opts.onStatus?.({ phase: 'success', message: opts.successMessage || 'Confirmed!', txHash });
        finish(resolve, receipt);
      })
      .on('error', (error) => {
        opts.onStatus?.({ phase: 'error', message: parseTxError(error), txHash });
        finish(reject, error);
      })
      .catch((error) => {
        opts.onStatus?.({ phase: 'error', message: parseTxError(error), txHash });
        finish(reject, error);
      });
    opts.onStatus?.({ phase: 'signing', message: 'Confirm in your wallet…', txHash: null });
  });
}

export function getTxExplorerUrl(txHash, explorerBase = 'https://basescan.org') {
  if (!txHash) return null;
  return `${explorerBase}/tx/${txHash}`;
}
